import { ReactNode, useState } from 'react';
import { Case, Switch } from 'core/common/ui/ui-kit';
import { prepareHtml } from 'core/common/utils/prepareHtml';
import { PaymentContent } from 'core/flow/entities/Content';
import {
  CardPaymentEntities,
  Currency,
  PaymentOrderDescription,
  PaymentProcessSteps,
  PaymentType,
} from 'core/payments/entities';
import { countriesWithoutPaypal, Locales } from 'core/user/entities';
import { Location, LocationOption } from 'core/user/ui';
import { PaymentFormFailed } from '../PaymentFormFailed';
import { PaymentLoader } from '../PaymentLoader';
import { PaymentMethods, PaymentMethodsWithoutPaypal } from '../PaymentMethods';
import { usePaymentForm } from './usePaymentForm';
import { TitleStyled, WrapperStyled } from './styled';

export type PaymentFormProps = {
  productId: string;
  onPaymentSuccess?: (data: { order?: PaymentOrderDescription }) => void;
  onPaymentFailed?: () => void;
  children?: ReactNode;
  currency?: Currency;
  content: PaymentContent;
};

export const PaymentForm = ({ children, content, ...otherProps }: PaymentFormProps) => {
  const {
    paymentError,
    shouldRenderPaypal,
    shouldRenderCardPayment,
    shouldRenderApplePay,
    paymentProcessStep,
    paymentFormPaypalData,
    paymentFormMerchantData,
    paymentFormFailed,
    trackOrderCardStartProcessing,
    trackOrderPaypalStartProcessing,
    paymentSucceed,
    paypalFailed,
    paymentVerify,
    cardFormError,
    paymentClick,
    tryAgain,
    funnelName,
  } = usePaymentForm(otherProps);

  const [paymentMethod, setPaymentMethod] = useState('');

  const onOrderCardStartProcessing = (paymentType: CardPaymentEntities) => {
    trackOrderCardStartProcessing(paymentType);
    setPaymentMethod(paymentType);
  };

  const onOrderPaypalStartProcessing = () => {
    trackOrderPaypalStartProcessing();
    setPaymentMethod(PaymentType.PAYPAL);
  };

  const onSucceed = (data: { order?: PaymentOrderDescription }) => {
    paymentSucceed(data, paymentMethod);
  };

  if (paymentProcessStep === PaymentProcessSteps.FAILED) {
    return (
      <PaymentFormFailed paymentError={paymentError} onTryAgain={tryAgain} content={content} />
    );
  }

  if (paymentProcessStep === PaymentProcessSteps.IN_PROCESS) {
    return (
      <WrapperStyled>
        <PaymentLoader />
      </WrapperStyled>
    );
  }
  return (
    <WrapperStyled>
      <TitleStyled variant="h4" dangerouslySetInnerHTML={prepareHtml(content.title)} />
      <Location>
        <LocationOption option={countriesWithoutPaypal}>
          <PaymentMethodsWithoutPaypal
            onPaymentClick={paymentClick}
            shouldRenderCardPayment={shouldRenderCardPayment}
            shouldRenderApplePay={shouldRenderApplePay}
            formMerchantData={paymentFormMerchantData}
            onFormFailed={paymentFormFailed}
            onCardFormError={cardFormError}
            onVerify={paymentVerify}
            onOrderCardStartProcessing={onOrderCardStartProcessing}
            onSucceed={onSucceed}
            content={content}
          >
            {children}
          </PaymentMethodsWithoutPaypal>
        </LocationOption>
        <LocationOption option={Locales.OTHER}>
          <Switch value={funnelName}>
            <Case value="attachment-style">
              <PaymentMethods
                onPaymentClick={paymentClick}
                shouldRenderPaypal={shouldRenderPaypal}
                shouldRenderCardPayment={shouldRenderCardPayment}
                shouldRenderApplePay={shouldRenderApplePay}
                formMerchantData={paymentFormMerchantData}
                onFormFailed={paymentFormFailed}
                onCardFormError={cardFormError}
                onVerify={paymentVerify}
                onOrderPaypalStartProcessing={onOrderPaypalStartProcessing}
                onOrderCardStartProcessing={onOrderCardStartProcessing}
                onSucceed={onSucceed}
                paypalPaymentUrl={paymentFormPaypalData}
                onPaypalFailed={paypalFailed}
                defaultPaymentMethod={PaymentType.CARD}
                content={content}
              >
                {children}
              </PaymentMethods>
            </Case>
            <Case value="attachment-style-zodiac">
              <PaymentMethods
                onPaymentClick={paymentClick}
                shouldRenderPaypal={shouldRenderPaypal}
                shouldRenderCardPayment={shouldRenderCardPayment}
                shouldRenderApplePay={shouldRenderApplePay}
                formMerchantData={paymentFormMerchantData}
                onFormFailed={paymentFormFailed}
                onCardFormError={cardFormError}
                onVerify={paymentVerify}
                onOrderPaypalStartProcessing={onOrderPaypalStartProcessing}
                onOrderCardStartProcessing={onOrderCardStartProcessing}
                onSucceed={onSucceed}
                paypalPaymentUrl={paymentFormPaypalData}
                onPaypalFailed={paypalFailed}
                defaultPaymentMethod={PaymentType.CARD}
                content={content}
              >
                {children}
              </PaymentMethods>
            </Case>
            <Case default>
              <PaymentMethods
                onPaymentClick={paymentClick}
                shouldRenderPaypal={shouldRenderPaypal}
                shouldRenderCardPayment={shouldRenderCardPayment}
                shouldRenderApplePay={shouldRenderApplePay}
                formMerchantData={paymentFormMerchantData}
                onFormFailed={paymentFormFailed}
                onCardFormError={cardFormError}
                onVerify={paymentVerify}
                onOrderPaypalStartProcessing={onOrderPaypalStartProcessing}
                onOrderCardStartProcessing={onOrderCardStartProcessing}
                onSucceed={onSucceed}
                paypalPaymentUrl={paymentFormPaypalData}
                onPaypalFailed={paypalFailed}
                defaultPaymentMethod={PaymentType.PAYPAL}
                content={content}
              >
                {children}
              </PaymentMethods>
            </Case>
          </Switch>
        </LocationOption>
      </Location>
    </WrapperStyled>
  );
};
